import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Finance & Banking',
  'Manufacturing',
  'Retail',
  'Education',
  'Government',
  'Consulting',
  'Insurance',
  'Other',
];

const ProjectSetupPage: React.FC = () => {
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setupUserProject, user, needsProjectSetup, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    setIsLoading(true);

    try {
      const result = await setupUserProject({
        company_name: companyName.trim(),
        industry: industry || undefined,
        description: description.trim() || undefined,
      });

      if (!result.success) {
        setError(result.error || 'Failed to setup project');
        return;
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Failed to setup project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const domain = user?.email?.split('@')[1] || '';

  // If user doesn't need project setup but doesn't have a project, they need an invite
  if (!needsProjectSetup && !user?.project) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>🔒 Access Required</h1>
            <p>Welcome, <strong>{user?.email}</strong></p>
          </div>

          <div className="info-box" style={{ margin: '20px 0' }}>
            <p>An organization already exists for <strong>@{domain}</strong>.</p>
            <p style={{ marginTop: '10px' }}>You'll need an invite from your organization's admin to join.</p>
            <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
              Contact your admin to request access.
            </p>
          </div>

          <button onClick={handleSignOut} className="login-button" style={{ marginTop: '20px' }}>
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card setup-card">
        <div className="login-header">
          <h1>🏢 Setup Your Organization</h1>
          <p>You're the first user from <strong>@{domain}</strong></p>
          <p className="subtitle">Complete the setup to become the admin</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="companyName">Company Name *</label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter your company name"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="industry">Industry</label>
            <select
              id="industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              disabled={isLoading}
            >
              <option value="">Select an industry</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of your organization (optional)"
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="info-box">
            <p>🔐 As the first user, you'll be the <strong>Admin</strong> of this organization. You can invite other team members after setup.</p>
          </div>

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? 'Setting up...' : 'Complete Setup'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProjectSetupPage;

